import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import 'survey-core/survey-core.css';
import { DefaultLightPanelless } from 'survey-core/themes';
import { ReactElementFactory, Survey } from 'survey-react-ui';
import { useETI } from '../hooks';
import { isETIV2 } from '../types';
import { ETIFilePreview } from './ETIFilePreview';
import './style.css';

// Register custom file preview component once (outside component)
ReactElementFactory.Instance.registerElement(
  'sv-file-preview',
  (props: any) => {
    const { question } = props;
    return <ETIFilePreview question={question} />;
  },
);

export function ETIAnswer() {
  const { taskId } = useParams();

  const { eti, isLoading: isETILoading, error } = useETI(Number(taskId));

  if (isETILoading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '90vh',
          flexDirection: 'column',
        }}
      >
        <CircularProgress color="info" />
        <Typography variant="h6" sx={{ mt: 4 }}>
          Loading Preview...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center">
        Failed to load survey preview.
      </Typography>
    );

  if (!isETIV2(eti)) {
    return (
      <Typography color="error" align="center">
        This version of ETI is not supported.
      </Typography>
    );
  }

  // Create SurveyJS model from fetched JSON
  const surveyJson = eti?.surveySchema;
  const survey = new Model(surveyJson);
  const ansData = eti?.answers?.data;
  survey.applyTheme(DefaultLightPanelless);

  // Add custom properties to survey so they're accessible in custom widgets
  (survey as any).policyId = eti?.policyId;
  (survey as any).taskId = eti?.taskId;

  // Render all questions on a single page
  survey.questionsOnPageMode = 'singlePage';
  survey.showNavigationButtons = 'none';
  survey.mode = 'display';

  survey.data = ansData;

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        maxWidth: '1280px',
        margin: '0 auto',
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
        }}
      >
        <Typography variant="h1" gutterBottom>
          ETI Response
        </Typography>
        <Typography variant="body2" gutterBottom>
          {`Policy/Task ID: ${eti?.policyId}/${eti?.taskId}`}
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Survey model={survey} />
      </Box>
    </Box>
  );
}
